// Hand-designed 3×3 kernels. The whole point: a CNN learns these numbers from
// data — here we just pick them by hand so the learner sees the raw mechanic.
// `div`  normalises the sum, `bias` shifts it (e.g. emboss centres on 128),
// `abs`  takes magnitude (edge detectors output ± values we want as brightness).
export const KERNELS = [
  {
    id: 'identity', label: 'Identity', desc: 'Copies the pixel — no change',
    k: [[0, 0, 0], [0, 1, 0], [0, 0, 0]], div: 1, bias: 0, abs: false,
  },
  {
    id: 'blur', label: 'Blur', desc: 'Averages neighbours — smooths detail',
    k: [[1, 1, 1], [1, 1, 1], [1, 1, 1]], div: 9, bias: 0, abs: false,
  },
  {
    id: 'sharpen', label: 'Sharpen', desc: 'Boosts centre vs neighbours',
    k: [[0, -1, 0], [-1, 5, -1], [0, -1, 0]], div: 1, bias: 0, abs: false,
  },
  {
    id: 'sobelX', label: 'Sobel X', desc: 'Detects vertical edges',
    k: [[-1, 0, 1], [-2, 0, 2], [-1, 0, 1]], div: 1, bias: 0, abs: true,
  },
  {
    id: 'sobelY', label: 'Sobel Y', desc: 'Detects horizontal edges',
    k: [[-1, -2, -1], [0, 0, 0], [1, 2, 1]], div: 1, bias: 0, abs: true,
  },
  {
    id: 'emboss', label: 'Emboss', desc: 'Lights one side — fake 3D relief',
    k: [[-2, -1, 0], [-1, 1, 1], [0, 1, 2]], div: 1, bias: 128, abs: false,
  },
]

export const getKernel = (id) => KERNELS.find(k => k.id === id) || KERNELS[0]

// Apply a kernel to a grayscale array (0-255), writing the result into `out`
// (a Uint8ClampedArray, length W*H). Border pixels are left at 0.
export function convolveGray(gray, W, H, kernel, out) {
  const { k, div, bias, abs } = kernel
  for (let y = 1; y < H - 1; y++) {
    for (let x = 1; x < W - 1; x++) {
      let sum = 0
      for (let ky = -1; ky <= 1; ky++)
        for (let kx = -1; kx <= 1; kx++)
          sum += gray[(y + ky) * W + (x + kx)] * k[ky + 1][kx + 1]
      let v = sum / div + bias
      if (abs) v = Math.abs(sum / div)
      out[y * W + x] = v
    }
  }
  return out
}

// Compute the single output value + the 9 contributing products for one pixel.
// Used by the "show the math" inset.
export function convolveAt(gray, W, x, y, kernel) {
  const { k, div, bias, abs } = kernel
  const cells = []
  let sum = 0
  for (let ky = -1; ky <= 1; ky++) {
    for (let kx = -1; kx <= 1; kx++) {
      const p = gray[(y + ky) * W + (x + kx)]
      const w = k[ky + 1][kx + 1]
      sum += p * w
      cells.push({ p, w })
    }
  }
  let v = sum / div + bias
  if (abs) v = Math.abs(sum / div)
  return { cells, sum, out: Math.max(0, Math.min(255, Math.round(v))) }
}
